import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { showRewardedAd } from '@/lib/ads/RewardedAds';
import { useAuth } from '@/lib/auth/AuthProvider';
import { queryKeys } from '@/lib/data/keys';
import {
  cancelChip,
  buyExtraSpin,
  grantAdExtraSpin,
  createMiniLeague,
  getLineupHistory,
  getMiniLeagueLeaderboard,
  getMyFantasyTeam,
  getMyRank,
  getGameweekLeaderboard,
  getOverallLeaderboard,
  getPlayerGwScores,
  getPublicSquad,
  getTeamGwScore,
  getTransferState,
  joinMiniLeague,
  leaveMiniLeague,
  listFantasyPlayerPool,
  listFantasySeasons,
  listGameweeks,
  listMyFantasyTeams,
  listMyMiniLeagues,
  listSquadPicks,
  listTeamChips,
  getSpinState,
  recordMissionEvent,
  takeSpin,
  playChip,
  saveSquad,
  type SquadPickInput,
} from '@/lib/data/provider';
import { useRealtimeInvalidation } from '@/lib/data/realtime';
import type { ChipType, Tables } from '@/lib/supabase/types';

export function useFantasySeasons() {
  return useQuery({
    queryKey: queryKeys.fantasySeasons,
    queryFn: listFantasySeasons,
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured,
  });
}

export function useGameweeks(fantasySeasonId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.gameweeks(fantasySeasonId ?? 'none'),
    queryFn: () => listGameweeks(fantasySeasonId as string),
    staleTime: QUERY_STALE_TIME.fixtures,
    enabled: isSupabaseConfigured && Boolean(fantasySeasonId),
  });
}

/** The gameweek the UI focuses on: live one, else next upcoming, else last. */
export function pickCurrentGameweek(
  gameweeks: Tables<'gameweeks'>[] | undefined,
): Tables<'gameweeks'> | null {
  if (!gameweeks || gameweeks.length === 0) return null;
  const live = gameweeks.find((gw) => gw.status === 'live');
  if (live) return live;
  const upcoming = gameweeks
    .filter((gw) => gw.status === 'upcoming')
    .sort((a, b) => a.deadline_at.localeCompare(b.deadline_at));
  if (upcoming.length > 0) return upcoming[0];
  return gameweeks[gameweeks.length - 1];
}

export function useMyFantasyTeams() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: queryKeys.myFantasyTeams(userId ?? 'anon'),
    queryFn: () => listMyFantasyTeams(userId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function useMyFantasyTeam(fantasySeasonId: string | undefined) {
  const { userId } = useAuth();
  return useQuery({
    queryKey: queryKeys.myFantasyTeam(fantasySeasonId ?? 'none', userId ?? 'anon'),
    queryFn: () => getMyFantasyTeam(fantasySeasonId as string, userId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(fantasySeasonId) && Boolean(userId),
  });
}

export function useSquadPicks(fantasyTeamId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.squadPicks(fantasyTeamId ?? 'none'),
    queryFn: () => listSquadPicks(fantasyTeamId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(fantasyTeamId),
  });
}

export function useFantasyPlayerPool(competitionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.fantasyPlayerPool(competitionId ?? 'none'),
    queryFn: () => listFantasyPlayerPool(competitionId as string),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && Boolean(competitionId),
  });
}

export function usePlayerGwScores(gameweekId: string | undefined) {
  const query = useQuery({
    queryKey: queryKeys.playerGwScores(gameweekId ?? 'none'),
    queryFn: () => getPlayerGwScores(gameweekId as string),
    staleTime: QUERY_STALE_TIME.live,
    enabled: isSupabaseConfigured && Boolean(gameweekId),
  });
  const byPlayer = useMemo(() => {
    const map = new Map<string, Tables<'player_gameweek_scores'>>();
    for (const row of query.data ?? []) {
      map.set(row.player_id, row);
    }
    return map;
  }, [query.data]);
  return { ...query, byPlayer };
}

export function useTeamGwScore(fantasyTeamId: string | undefined, gameweekId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.teamGwScore(fantasyTeamId ?? 'none', gameweekId ?? 'none'),
    queryFn: () => getTeamGwScore(fantasyTeamId as string, gameweekId as string),
    staleTime: QUERY_STALE_TIME.live,
    enabled: isSupabaseConfigured && Boolean(fantasyTeamId) && Boolean(gameweekId),
  });
}

export function useLineupHistory(
  fantasyTeamId: string | undefined,
  gameweekId: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.lineupHistory(fantasyTeamId ?? 'none', gameweekId ?? 'none'),
    queryFn: () => getLineupHistory(fantasyTeamId as string, gameweekId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(fantasyTeamId) && Boolean(gameweekId),
  });
}

/** Live points land via realtime on the scores tables. */
export function useFantasyScoresRealtime(gameweekId: string | undefined) {
  useRealtimeInvalidation({
    channel: `gw-scores-${gameweekId ?? 'none'}`,
    table: 'player_gameweek_scores',
    filter: gameweekId ? `gameweek_id=eq.${gameweekId}` : undefined,
    invalidate: gameweekId
      ? [queryKeys.playerGwScores(gameweekId), ['fantasy', 'team-gw-score']]
      : [],
    enabled: isSupabaseConfigured && Boolean(gameweekId),
  });
}

export function useOverallLeaderboard(fantasySeasonId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.overallLeaderboard(fantasySeasonId ?? 'none'),
    queryFn: () => getOverallLeaderboard(fantasySeasonId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(fantasySeasonId) && enabled,
  });
}

export function useGameweekLeaderboard(gameweekId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['gameweek-leaderboard', gameweekId ?? 'none'],
    queryFn: () => getGameweekLeaderboard(gameweekId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(gameweekId) && enabled,
  });
}

export function useMyRank(fantasySeasonId: string | undefined) {
  const { userId } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.myRank(fantasySeasonId ?? 'none'), userId ?? 'anon'],
    queryFn: () => getMyRank(fantasySeasonId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(fantasySeasonId) && Boolean(userId),
  });
}

export function useMyMiniLeagues() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: queryKeys.myMiniLeagues(userId ?? 'anon'),
    queryFn: () => listMyMiniLeagues(userId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function useMiniLeagueLeaderboard(leagueId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.miniLeagueLeaderboard(leagueId ?? 'none'),
    queryFn: () => getMiniLeagueLeaderboard(leagueId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(leagueId),
  });
}

/** A rival's revealed squad for the leaderboard squad-view modal. */
export function usePublicSquad(fantasyTeamId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.publicSquad(fantasyTeamId ?? 'none'),
    queryFn: () => getPublicSquad(fantasyTeamId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(fantasyTeamId),
  });
}

/** Chips the team has armed/used (one per gameweek, each chip once per season). */
export function useTeamChips(fantasyTeamId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.teamChips(fantasyTeamId ?? 'none'),
    queryFn: () => listTeamChips(fantasyTeamId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(fantasyTeamId),
  });
}

/** Live transfer picture: changes vs the last locked squad + the projected hit. */
export function useTransferState(fantasySeasonId: string | undefined) {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['fantasy', 'transfers', fantasySeasonId ?? 'none', userId ?? 'anon'],
    queryFn: () => getTransferState(fantasySeasonId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(fantasySeasonId) && Boolean(userId),
  });
}

export function usePlayChip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { gameweekId: string; chipType: ChipType }) =>
      playChip(input.gameweekId, input.chipType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'chips'] });
    },
  });
}

export function useCancelChip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (gameweekId: string) => cancelChip(gameweekId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'chips'] });
    },
  });
}

export function useSaveSquad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { fantasySeasonId: string; teamName: string; picks: SquadPickInput[] }) =>
      saveSquad(input.fantasySeasonId, input.teamName, input.picks),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy'] });
    },
  });
}

/** Spin Boost state: cooldown, live-lock and the team's active 1-2 spin picks. */
export function useSpinState(fantasySeasonId: string | undefined) {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['fantasy', 'spin', fantasySeasonId ?? 'none', userId ?? 'anon'],
    queryFn: () => getSpinState(fantasySeasonId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(fantasySeasonId) && Boolean(userId),
  });
}

export function useTakeSpin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { fantasySeasonId: string; playerIds: string[] }) =>
      takeSpin(input.fantasySeasonId, input.playerIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'spin'] });
      void recordMissionEvent('spin')
        .then(() => queryClient.invalidateQueries({ queryKey: ['rewards'] }))
        .catch(() => {});
    },
  });
}

/** Watch a rewarded ad to clear the spin cooldown for one extra spin. */
export function useGrantAdExtraSpin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fantasySeasonId: string) => {
      const { earned } = await showRewardedAd();
      if (!earned) throw new Error('AD_NOT_COMPLETED');
      return grantAdExtraSpin(fantasySeasonId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'spin'] });
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
    },
  });
}

/** Spend coins to clear the spin cooldown for one extra spin (no ad). */
export function useBuyExtraSpin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fantasySeasonId: string) => buyExtraSpin(fantasySeasonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'spin'] });
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
    },
  });
}

export function useCreateMiniLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { fantasySeasonId: string; name: string }) =>
      createMiniLeague(input.fantasySeasonId, input.name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'mini-leagues'] });
    },
  });
}

export function useJoinMiniLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => joinMiniLeague(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'mini-leagues'] });
    },
  });
}

export function useLeaveMiniLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leagueId: string) => leaveMiniLeague(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'mini-leagues'] });
    },
  });
}
